import React from 'react';
import { Tooltip } from '@material-ui/core';
import '../style.css';

export const normalizeDatosExtra = (value = '') => value
  .replace(/<\/?br\s*\/?>/gi, '\n')
  .replace(/\r\n/g, '\n')
  .replace(/\\n/g, '\n');

const getInlineSegments = (text = '') => {
  const segments = [];
  const boldRegex = /<b>(.*?)<\/b>|\*\*(.+?)\*\*/gi;
  let lastIndex = 0;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }

    segments.push({
      type: 'bold',
      value: match[1] || match[2] || ''
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: 'text', value: text }];
};

export const getDatosExtraSegments = (value = '') => {
  const normalizedText = normalizeDatosExtra(value);
  const lines = normalizedText.split('\n');
  const segments = [];

  lines.forEach((line, index) => {
    segments.push(...getInlineSegments(line));

    if (index < lines.length - 1) {
      segments.push({ type: 'break', value: '\n' });
    }
  });

  return segments;
};

export const stripDatosExtraFormatting = (value = '') => getDatosExtraSegments(value)
  .map((segment) => (segment.type === 'break' ? '\n' : segment.value))
  .join('');

export const truncateDatosExtraSegments = (segments = [], maxLength = 0) => {
  if (!Number.isFinite(maxLength) || maxLength <= 0) {
    return [];
  }

  let remaining = maxLength;
  const truncated = [];

  for (const segment of segments) {
    if (remaining <= 0) {
      break;
    }

    if (segment.type === 'break') {
      truncated.push(segment);
      remaining -= 1;
      continue;
    }

    if (segment.value.length <= remaining) {
      truncated.push(segment);
      remaining -= segment.value.length;
      continue;
    }

    truncated.push({
      ...segment,
      value: segment.value.slice(0, remaining)
    });
    remaining = 0;
  }

  return truncated;
};

export const renderDatosExtraSegments = (segments = [], keyPrefix = 'datos-extra') => segments.map((segment, index) => {
  if (segment.type === 'break') {
    return <br key={`${keyPrefix}-break-${index}`} />;
  }

  if (segment.type === 'bold') {
    return (
      <strong key={`${keyPrefix}-bold-${index}`}>
        {segment.value}
      </strong>
    );
  }

  return <React.Fragment key={`${keyPrefix}-text-${index}`}>{segment.value}</React.Fragment>;
});

export const renderDatosExtra = (datosExtra, { multiline = true } = {}) => {
  const segments = getDatosExtraSegments(multiline ? datosExtra : normalizeDatosExtra(datosExtra).replace(/\n+/g, ' '));
  return renderDatosExtraSegments(segments);
};

const DatosExtra = ({
  datosExtra,
  className = 'datoExtra',
  multiline = false,
  tooltip = true,
  contentRef,
  onOverflowChange,
  children
}) => {
  const ref = React.useRef();
  const [hasOverflow, setHasOverflow] = React.useState(false);

  React.useLayoutEffect(() => {
    if (ref.current) {
      const nextHasOverflow = (
        ref.current.scrollWidth > ref.current.clientWidth ||
        ref.current.scrollHeight > ref.current.clientHeight
      );

      setHasOverflow(nextHasOverflow);

      if (onOverflowChange) {
        onOverflowChange(nextHasOverflow);
      }
    }
  }, [datosExtra, multiline, className, onOverflowChange]);

  React.useLayoutEffect(() => {
    if (!contentRef) {
      return undefined;
    }

    if (typeof contentRef === 'function') {
      contentRef(ref.current);
      return () => contentRef(null);
    }

    contentRef.current = ref.current;
    return () => {
      contentRef.current = null;
    };
  }, [contentRef]);

  const tooltipContent = renderDatosExtra(datosExtra, { multiline: true });

  const child = React.useMemo(() => (
    <div className={className} ref={ref}>
      {renderDatosExtra(datosExtra, { multiline })}
      {children}
    </div>
  ), [children, className, datosExtra, multiline]);

  return (
    <div>
      {tooltip && hasOverflow ? <Tooltip title={tooltipContent} arrow>{child}</Tooltip> : child}
    </div>
  );
};

export default DatosExtra;
